import {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
  type ReactNode,
} from "react"
import { ArrowLeft, ArrowUpRight, BotIcon, Coins } from "lucide-react"
import { HTTPError } from "ky"
import { useTranslation } from "react-i18next"
import { Link, useNavigate, useSearchParams } from "react-router"

import {
  Button,
  Separator,
  useSidebar,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@f2e/ui/components/base"
import type { AutosizeTextAreaRef } from "@f2e/ui/components/base-plus"
import { ScrollToBottomButton } from "@f2e/ui/components/Button"
import {
  useChatSessionV2,
  type ConversationPartApiResponse,
} from "@f2e/ui/chat-v2"
import type { FeedbackBlockedReason } from "@f2e/ui/components/Chat"
import { parsePaymentSummaryContent } from "@f2e/ui/components/Chat"
import { useScrollToBottom } from "@f2e/ui/hooks"
import { cn } from "@f2e/ui/lib/utils"

import Clock from "@/components/Clock"
import WorkspaceBadge from "@/components/WorkspaceBadge"
import { useStore } from "@/store"
import { LoadingPage, ErrorPage } from "@/components/System"
import {
  PendingInteractionPaymentCard,
  WebConversationPartPanel,
} from "@/components/Conversation"
import { CopilotHiddenField } from "@/components/Copilot"
import { ConversationSettings, EvaluationCaseLabel, History } from "@/types"
import {
  useGetHistoryApi,
  historyResponseToHistory,
  useCreatePlatformReplyApi,
  useListAIDraftsApi,
  historyAIDraftTarget,
  type AIDraftSummaryResponse,
  type PlatformReplyRejectionResponse,
  type PlatformReplyResponse,
} from "@/hooks/api"
import { FetchStatus } from "@/hooks/useFetch"
import {
  useToast,
  useSysConfig,
  useConversationEval,
  ConversationErrorType,
} from "@/hooks"
import { isOrgBudgetErrorCode, SOMETHING_WRONG_MSG } from "@/constants/errors"
import { DEFAULT_LOADING_MESSAGE, SETTINGS_ENDPOINTS } from "@/constants"
import {
  downloadAttachmentResource,
  loadAttachmentPreviewResource,
  resolveAttachmentResourceUrl,
} from "@/lib/attachmentResource"
import { toUIHistory } from "@/lib/chatMapper"
import { PaymentRequiredDialog } from "@/components/Billing"
import { redirectToLoginForExpiredSession } from "@/lib/authRedirect"
import {
  getUserFacingApiErrorMessage,
  normalizeApiError,
} from "@/lib/apiErrors"

import { CaseDetailModal } from "@/components/Evaluation"
import {
  evaluationCaseLabelResponseToData,
  useListEvaluationCaseLabelsApi,
} from "@/hooks/api/eval"
import {
  PlatformReplyComposerMeta,
  type PlatformReplyComposerMode,
} from "./PlatformReplyComposerMeta"
import {
  getPlatformReplyActorDisplayName,
  getPlatformReplyErrorMessage,
  hasSentPlatformReply,
  isPartialPlatformReply,
} from "./platformReplyMetadata"
import {
  AIDraftVersionSwitcher,
  type AppliedDraftComposerState,
} from "./AIDraft"
import { AIDraftPlatformReplyComposer } from "./AIDraftPlatformReplyComposer"
import { LineQuotaNotice } from "./LineQuotaNotice"
import { buildAIDraftTimelineGroups } from "./aiDraftTimelineGroups"
import { createPlatformReplySubmitIntent } from "./platformReplyIdempotency"
import { buildPlatformReplyRequest } from "./platformReplyRequest"
import { PlatformReplyAttachment } from "./PlatformReplyAttachment"
import { useHistoryScopedPlatformReplyNotice } from "./useHistoryScopedPlatformReplyNotice"

/**
 * Copilot-aware history detail surface.
 *
 * This component exposes the selected history record to Copilot through
 * CopilotHiddenField and WebConversationPartPanel's copilotFieldPrefix. When
 * changing selected-history behavior or conversation rendering, keep
 * workspace.history page context XML in sync and preserve the fields needed by
 * lookup_history: selected history id, selected title, and conversation
 * summaries.
 */

const formatCredits = (value: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value)

export const HISTORY_DETAIL_SCROLL_CONTAINER = "history-detail"
const HUMAN_REPLY_HISTORY_POLL_INTERVAL_MS = 5000
const HUMAN_REPLY_HISTORY_POLL_FAILURE_VISIBLE_THRESHOLD = 3
const DRAFT_CONTEXT_STALE_CODE = "draft_context_stale"
const PLATFORM_REPLY_FORM_ERROR_MESSAGE_KEYS: Record<string, string> = {
  form_proposal_unavailable: "history.aiDraft.form.errors.proposalUnavailable",
  invalid_form_prefill: "history.aiDraft.form.errors.invalidPrefill",
  channel_form_unsupported: "history.aiDraft.form.errors.channelUnsupported",
  idempotency_key_conflict: "history.aiDraft.form.errors.idempotencyConflict",
}

const createPlatformReplyIdempotencyKey = () => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID()
  }
  return `platform-reply-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const CONVERSATION_ERROR_TITLE_KEYS: Record<ConversationErrorType, string> = {
  [ConversationErrorType.RefreshConversationFailed]:
    "common.conversation.errors.refreshConversationFailed",
  [ConversationErrorType.SSEError]: "common.conversation.errors.sseError",
}

export interface HistoryDetailProps {
  historyId: number
  mode?: "standalone" | "embedded"
  onPlatformReplySent?: () => void
  readOnly?: boolean
  headerActions?: ReactNode
}

const buildConversationSettings = ({
  currentSettings,
  history,
  agentHistoryId,
}: {
  currentSettings?: ConversationSettings
  history?: History
  agentHistoryId?: string
}): ConversationSettings => ({
  ...currentSettings,
  agentHistoryId: history?.initAgentId ? agentHistoryId : undefined,
})

const getPartAgentHistoryId = (metadata: Record<string, unknown>) => {
  const agentHistoryId = metadata.agent_history_id ?? metadata.agentHistoryId
  return typeof agentHistoryId === "string" ? agentHistoryId : undefined
}

const isPlatformReplyPart = (part: ConversationPartApiResponse) =>
  part.part_kind === "text" && part.metadata.source === "platform_reply"

export const isLiveTestCompatibilityHistory = (history?: object) =>
  Boolean(
    history &&
    "sourceType" in history &&
    (history as { sourceType?: unknown }).sourceType === "console"
  )

const getPlatformReplyStatus = (part: ConversationPartApiResponse) => {
  const platformReply = part.metadata.platform_reply
  if (!platformReply || typeof platformReply !== "object") return undefined
  const status = (platformReply as { status?: unknown }).status
  return typeof status === "string" ? status : undefined
}

const getPlatformReplyChannelType = (part: ConversationPartApiResponse) => {
  const deliveryChannel = part.metadata.delivery_channel
  if (!deliveryChannel || typeof deliveryChannel !== "object") return undefined
  const channelType = (deliveryChannel as { channel_type?: unknown })
    .channel_type
  return typeof channelType === "string" && channelType.trim()
    ? channelType
    : undefined
}

const isPlatformReplySuccessResponse = (
  value: PlatformReplyResponse | PlatformReplyRejectionResponse | undefined
): value is PlatformReplyResponse =>
  !!value && "status" in value && "conversation_part_id" in value

export const HistoryDetail = ({
  historyId,
  mode = "standalone",
  onPlatformReplySent,
  readOnly = false,
  headerActions,
}: HistoryDetailProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { billingUiEnabled } = useSysConfig()
  const [searchParams] = useSearchParams()
  const { alertToast, successToast } = useToast()
  const { isMobile, setOpen: setSidebarOpen } = useSidebar()
  const isEmbedded = mode === "embedded"
  const scrollContainerSelector = isEmbedded
    ? `[data-scroll-container="${HISTORY_DETAIL_SCROLL_CONTAINER}"]`
    : '[data-scroll-container="main"]'

  const [setStoreCurrentHistory, sysConfig] = useStore((state) => [
    state.setCurrentHistory,
    state.sysConfig,
  ])
  const [isEvaluationContextView, setIsEvaluationContextView] =
    useState<boolean>(false)
  const isPollingConversationsRef = useRef(false)
  const [showPaymentRequired, setShowPaymentRequired] = useState(false)
  const requestedHistoryIdRef = useRef<number | null>(null)
  const latestHistoryBackgroundRequestIdRef = useRef(0)
  const latestAIDraftRequestIdRef = useRef(0)
  const activeHistoryIdRef = useRef<number | null>(null)
  const [platformReplyText, setPlatformReplyText] = useState("")
  const [appliedDraftContext, setAppliedDraftContext] =
    useState<AppliedDraftComposerState | null>(null)
  const appliedDraftId = appliedDraftContext?.id ?? null
  const [aiDraftSummaries, setAIDraftSummaries] = useState<
    AIDraftSummaryResponse[]
  >([])
  const platformReplyTextareaRef = useRef<AutosizeTextAreaRef>(null)
  const { notice: platformReplyNotice, setNotice: setPlatformReplyNotice } =
    useHistoryScopedPlatformReplyNotice(historyId)
  const platformReplySubmitIntentRef = useRef(
    createPlatformReplySubmitIntent(createPlatformReplyIdempotencyKey)
  )
  const platformReplySubmitIntent = platformReplySubmitIntentRef.current
  const pendingSentPlatformReplyPartIdRef = useRef<number | null>(null)
  const [composerMode, setComposerMode] =
    useState<PlatformReplyComposerMode>("platformReply")
  const [pollingFailureCount, setPollingFailureCount] = useState(0)
  const [labels, setLabels] = useState<EvaluationCaseLabel[]>([])

  useEffect(() => {
    activeHistoryIdRef.current = historyId
    latestHistoryBackgroundRequestIdRef.current += 1
    latestAIDraftRequestIdRef.current += 1
    setPlatformReplyText("")
    setAppliedDraftContext(null)
    platformReplySubmitIntent.invalidate()
    setAIDraftSummaries([])
    pendingSentPlatformReplyPartIdRef.current = null
  }, [historyId, platformReplySubmitIntent])

  function onFinishConversationCallback(historyId: number) {
    const requestId = latestHistoryBackgroundRequestIdRef.current + 1
    latestHistoryBackgroundRequestIdRef.current = requestId

    void fetchHistoryInBackground({
      urlBuilderParams: { historyId },
      preserveData: true,
      callbackFn: (resp) => {
        if (!resp?.data) return
        if (requestId !== latestHistoryBackgroundRequestIdRef.current) return
        if (activeHistoryIdRef.current !== historyId) return
        const nextHistory = historyResponseToHistory(resp.data)
        setHistoryInfo(nextHistory)
        if (!isEmbedded) {
          setStoreCurrentHistory(nextHistory)
        }
      },
    }).catch((error) => {
      console.warn("failed to refresh history summary", error)
    })
  }

  function onConversationErrorCallback(
    errorType: ConversationErrorType,
    rawMessage?: string
  ) {
    console.error("handleConversationError", errorType, rawMessage)

    const match = rawMessage?.match(/^\[code=(\d+)\]\s*(.*)$/)
    const code = match ? Number(match[1]) : null
    const message = match ? match[2] : rawMessage

    if (
      errorType === ConversationErrorType.SSEError &&
      isOrgBudgetErrorCode(code)
    ) {
      setShowPaymentRequired(true)
      return
    }

    const titleKey = CONVERSATION_ERROR_TITLE_KEYS[errorType]
    alertToast({
      title: titleKey ? t(titleKey) : t(SOMETHING_WRONG_MSG),
      description:
        errorType === ConversationErrorType.RefreshConversationFailed
          ? message
          : message || t("constants.errors.backToHome"),
    })
  }

  const {
    context: currentContext,
    history: currentHistory,
    isAsking,
    parts,
    replyContextAnchor,
    loadConversationsError,
    loadConversations,
    refreshConversations,
    setHistoryInfo,
    changeSettings,
    submitFeedback,
    // HITL
    pendingPayment,
    isHitlPending,
    submittingFormRequestId,
    submitFormInteraction,
    rejectFormInteraction,
    clearPendingInteraction,
  } = useChatSessionV2({
    getBaseUrl: () => `${sysConfig.serverRoot}/api/v1`,
    onError: onConversationErrorCallback,
    onUnauthorized: redirectToLoginForExpiredSession,
    onComplete: (nextHistoryId) => {
      if (nextHistoryId) {
        onFinishConversationCallback(nextHistoryId)
      }
    },
    messageWindow: "latest",
  })
  const aiDraftToolSupport = (currentHistory as History | undefined)
    ?.aiDraftToolSupport
  const refreshConversationsRef = useRef(refreshConversations)

  useEffect(() => {
    refreshConversationsRef.current = refreshConversations
  }, [refreshConversations])

  // Scroll tracking switches to the local detail pane when embedded.
  const {
    showScrollToBottom,
    scrollToBottom,
    triggerAutoScroll,
    checkScrollPosition,
  } = useScrollToBottom({
    selector: scrollContainerSelector,
    hasContent: parts.length > 0,
  })
  const resolveAttachmentUrl = useCallback(
    (url: string) =>
      resolveAttachmentResourceUrl(url, sysConfig.serverRoot, {
        organizationId: currentHistory?.workspace.organizationId,
        workspaceId: currentHistory?.workspace.id,
      }),
    [
      currentHistory?.workspace.id,
      currentHistory?.workspace.organizationId,
      sysConfig.serverRoot,
    ]
  )
  const handleAttachmentDownload = useCallback(
    (file: { url?: string; name: string }) =>
      downloadAttachmentResource(file, sysConfig.serverRoot),
    [sysConfig.serverRoot]
  )
  const handleAttachmentPreviewLoad = useCallback(
    (file: { url?: string; name: string }) =>
      loadAttachmentPreviewResource(file, sysConfig.serverRoot),
    [sysConfig.serverRoot]
  )

  const { error: getHistoryError, fetchFn: getHistory } =
    useGetHistoryApi(historyId)
  const { status: createPlatformReplyStatus, fetchFn: createPlatformReply } =
    useCreatePlatformReplyApi(historyId)
  const draftTarget = useMemo(
    () => historyAIDraftTarget(historyId),
    [historyId]
  )
  const { fetchFn: listAIDrafts } = useListAIDraftsApi(draftTarget)
  const { fetchFn: fetchHistoryInBackground } = useGetHistoryApi(
    -1,
    (params) => `/histories/${params.historyId}`
  )
  const normalizedHistoryError = normalizeApiError(getHistoryError)

  const refreshCurrentHistoryPermissions = useCallback(() => {
    const requestedHistoryId = historyId
    const requestId = latestHistoryBackgroundRequestIdRef.current + 1
    latestHistoryBackgroundRequestIdRef.current = requestId

    void fetchHistoryInBackground({
      urlBuilderParams: { historyId: requestedHistoryId },
      cache: "no-store",
      preserveData: true,
      callbackFn: (resp) => {
        if (!resp?.data) return
        if (requestId !== latestHistoryBackgroundRequestIdRef.current) return
        if (activeHistoryIdRef.current !== requestedHistoryId) return
        const nextHistory = historyResponseToHistory(resp.data)
        setHistoryInfo(nextHistory)
        if (!isEmbedded) {
          setStoreCurrentHistory(nextHistory)
        }
      },
    }).catch((error) => {
      if (
        requestId === latestHistoryBackgroundRequestIdRef.current &&
        activeHistoryIdRef.current === requestedHistoryId &&
        normalizeApiError(error).kind !== "permission_denied"
      ) {
        console.warn("failed to refresh History permissions", error)
      }
    })
  }, [
    fetchHistoryInBackground,
    historyId,
    isEmbedded,
    setHistoryInfo,
    setStoreCurrentHistory,
  ])

  const refreshAIDrafts = useCallback(async () => {
    const requestedHistoryId = historyId
    const requestId = latestAIDraftRequestIdRef.current + 1
    latestAIDraftRequestIdRef.current = requestId
    try {
      const limit = 100
      let offset = 0
      const summaries: AIDraftSummaryResponse[] = []
      while (true) {
        const response = await listAIDrafts({
          cache: "no-store",
          searchParams: new URLSearchParams({
            limit: String(limit),
            offset: String(offset),
          }),
          preserveData: true,
        })
        if (
          activeHistoryIdRef.current !== requestedHistoryId ||
          latestAIDraftRequestIdRef.current !== requestId
        )
          return
        summaries.push(...(response?.data ?? []))
        const total = response?.pagination?.total_records ?? summaries.length
        if (summaries.length >= total || (response?.data?.length ?? 0) < limit)
          break
        offset += limit
      }
      if (
        activeHistoryIdRef.current !== requestedHistoryId ||
        latestAIDraftRequestIdRef.current !== requestId
      )
        return
      setAIDraftSummaries(summaries)
    } catch (error) {
      if (activeHistoryIdRef.current === requestedHistoryId) {
        console.warn("failed to load AI Draft records", error)
      }
    }
  }, [historyId, listAIDrafts])

  useEffect(() => {
    void refreshAIDrafts()
  }, [refreshAIDrafts])

  const historyAgentId =
    currentHistory?.agentId ||
    (currentHistory?.meta?.conversation_agent_id as string | undefined) ||
    currentHistory?.initAgentId ||
    null

  const {
    isEvalModalOpen,
    evalCaseData,
    evaluators,
    handleCreateEvalFromParts,
    handleCloseEvalModal,
  } = useConversationEval()
  const workspaceId = currentHistory?.workspace?.id
  const { data: labelsResp, fetchFn: fetchLabels } =
    useListEvaluationCaseLabelsApi(workspaceId)

  useEffect(() => {
    if (isEvalModalOpen && workspaceId) {
      fetchLabels()
    }
  }, [fetchLabels, isEvalModalOpen, workspaceId])

  useEffect(() => {
    if (labelsResp?.data) {
      setLabels(labelsResp.data.map(evaluationCaseLabelResponseToData))
    }
  }, [labelsResp])

  const filteredParts = useMemo(() => {
    const throughPartParam = searchParams.get("through_part_id")
    const targetPartId = throughPartParam
      ? parseInt(throughPartParam, 10)
      : Number.NaN
    if (Number.isFinite(targetPartId) && parts.length > 0) {
      const targetPart = parts.find((part) => part.id === targetPartId)
      if (!targetPart) return parts

      const includedGroupIds = new Set<string>([
        targetPart.conversation_group_id,
      ])
      for (const part of parts) {
        if (part.sequence <= targetPart.sequence) {
          includedGroupIds.add(part.conversation_group_id)
        }
      }
      return parts.filter((part) =>
        includedGroupIds.has(part.conversation_group_id)
      )
    }

    const beforeParam = searchParams.get("before")
    if (!beforeParam || parts.length === 0) return []

    const targetConversationId = parseInt(beforeParam, 10)
    if (isNaN(targetConversationId)) return []

    const includedGroupIds = new Set<string>()
    for (const part of parts) {
      const comparableId = part.conversation_id ?? part.id
      if (
        comparableId !== undefined &&
        comparableId !== null &&
        comparableId < targetConversationId
      ) {
        includedGroupIds.add(part.conversation_group_id)
      }
    }

    return parts.filter((part) =>
      includedGroupIds.has(part.conversation_group_id)
    )
  }, [searchParams, parts])

  const hasBeforeFilter = useMemo(() => {
    const beforeParam = searchParams.get("before")
    return !!beforeParam && !isNaN(parseInt(beforeParam, 10))
  }, [searchParams])

  const hasThroughPartFilter = useMemo(() => {
    const throughPartParam = searchParams.get("through_part_id")
    return !!throughPartParam && !isNaN(parseInt(throughPartParam, 10))
  }, [searchParams])

  useEffect(() => {
    // `before` narrows the visible thread; only the explicit view flag enables the minimal eval-only UI.
    setIsEvaluationContextView(
      hasBeforeFilter && searchParams.get("view") === "evaluation-context"
    )
  }, [hasBeforeFilter, searchParams])

  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false)
    }
  }, [isMobile, setSidebarOpen])

  useEffect(() => {
    // Guard against repeated requests caused by re-renders/dependency churn.
    if (requestedHistoryIdRef.current === historyId) return
    requestedHistoryIdRef.current = historyId

    console.debug("existing history")

    getHistory({
      callbackFn: (resp) => {
        if (!resp || !resp.data) return

        const history = historyResponseToHistory(resp.data)
        if (!isEmbedded) {
          setStoreCurrentHistory(history)
        }
        void loadConversations(history)
          .then((loadedParts) => {
            if (!loadedParts) return
            const latestUserPartWithAgentHistoryId = [...loadedParts]
              .reverse()
              .find(
                (part) =>
                  part.part_kind === "user-prompt" &&
                  getPartAgentHistoryId(part.metadata)
              )
            const fallbackPartWithAgentHistoryId = loadedParts.find((part) =>
              getPartAgentHistoryId(part.metadata)
            )
            const agentHistoryId =
              (latestUserPartWithAgentHistoryId
                ? getPartAgentHistoryId(
                    latestUserPartWithAgentHistoryId.metadata
                  )
                : undefined) ??
              (fallbackPartWithAgentHistoryId
                ? getPartAgentHistoryId(fallbackPartWithAgentHistoryId.metadata)
                : undefined)

            changeSettings(
              buildConversationSettings({
                currentSettings: currentContext?.settings,
                history,
                agentHistoryId,
              })
            )
          })
          .catch((error) => {
            console.warn("failed to load history conversations", error)
          })
      },
    })
  }, [
    historyId,
    getHistory,
    loadConversations,
    isEmbedded,
    setStoreCurrentHistory,
    changeSettings,
    currentContext?.settings,
  ])

  useEffect(() => {
    if (isEmbedded) return
    if (!currentHistory) return
    setStoreCurrentHistory(currentHistory)
  }, [currentHistory, isEmbedded, setStoreCurrentHistory])

  const handlePlatformReplyTextChange = useCallback(
    (value: string) => {
      platformReplySubmitIntent.invalidate()
      setPlatformReplyText(value)
    },
    [platformReplySubmitIntent]
  )

  const handleAppliedDraftChange = useCallback(
    (draft: AppliedDraftComposerState | null) => {
      platformReplySubmitIntent.invalidate()
      setAppliedDraftContext(draft)
    },
    [platformReplySubmitIntent]
  )

  const handleSendPlatformReply = useCallback(
    async (attachmentId?: string, onAccepted?: () => void) => {
      const content = platformReplyText.trim()
      if (
        (!content && !attachmentId) ||
        createPlatformReplyStatus === FetchStatus.Loading
      )
        return
      const formPrefilledValues = appliedDraftContext?.form?.values ?? null
      const idempotencyKey = platformReplySubmitIntent.getKey()

      setPlatformReplyNotice(null)
      try {
        const resp = await createPlatformReply({
          jsonBody: buildPlatformReplyRequest({
            content,
            attachmentId,
            idempotencyKey,
            appliedDraftId,
            observedContextAnchor: replyContextAnchor,
            formPrefilledValues,
          }),
        })
        const data = resp?.data
        if (isPlatformReplySuccessResponse(data)) {
          handleAppliedDraftChange(null)
          pendingSentPlatformReplyPartIdRef.current =
            data.status === "sending" ? data.conversation_part_id : null
          if (data.status === "sent") onPlatformReplySent?.()
          if (data.status === "failed") {
            setPlatformReplyNotice({
              type: "failed",
              message:
                getPlatformReplyErrorMessage(data, t) ||
                data.error_code ||
                t("history.platformReply.deliveryFailedFallback"),
            })
          } else {
            setPlatformReplyText("")
            onAccepted?.()
          }
          refreshConversations()
          void refreshAIDrafts()
          return
        }

        if (data?.message) {
          setPlatformReplyNotice({
            type: "error",
            message: data.message,
          })
        }
      } catch (error) {
        const normalizedError = normalizeApiError(error)
        const rejectionCode =
          normalizedError.data &&
          typeof normalizedError.data === "object" &&
          "code" in normalizedError.data
            ? (normalizedError.data as { code?: unknown }).code
            : undefined
        if (rejectionCode === DRAFT_CONTEXT_STALE_CODE) {
          setPlatformReplyNotice({
            type: "error",
            message: t("history.aiDraft.sendContextStale"),
          })
          try {
            await refreshConversations({ silent: true })
            setPlatformReplyNotice(null)
          } catch {
            // Keep the actionable stale-context notice visible until polling recovers.
          }
          return
        }
        const formErrorKey =
          typeof rejectionCode === "string"
            ? PLATFORM_REPLY_FORM_ERROR_MESSAGE_KEYS[rejectionCode]
            : undefined
        if (formErrorKey) {
          setPlatformReplyNotice({ type: "error", message: t(formErrorKey) })
          return
        }
        const message = getUserFacingApiErrorMessage(
          error,
          t,
          t("history.platformReply.submitFailedFallback")
        )
        setPlatformReplyNotice({ type: "error", message })
      }
    },
    [
      createPlatformReply,
      createPlatformReplyStatus,
      appliedDraftContext,
      appliedDraftId,
      handleAppliedDraftChange,
      platformReplySubmitIntent,
      platformReplyText,
      replyContextAnchor,
      refreshAIDrafts,
      refreshConversations,
      setPlatformReplyNotice,
      onPlatformReplySent,
      t,
    ]
  )

  const handleResolvePaymentInteraction = useCallback(() => {
    clearPendingInteraction()
    refreshConversations()
  }, [clearPendingInteraction, refreshConversations])

  const handleHelpfulFeedback = useCallback(
    async (part: ConversationPartApiResponse) => {
      const success = await submitFeedback(part.id, "sys_helpful")
      if (success) {
        successToast({
          description: t(
            "common.chat.feedback.thankYou",
            "Thank you for your feedback!"
          ),
        })
      } else {
        alertToast({
          title: t("common.chat.feedback.failed", "Failed to submit feedback"),
        })
      }
      return success
    },
    [alertToast, submitFeedback, successToast, t]
  )

  const handleImproveFeedback = useCallback(
    async (
      part: ConversationPartApiResponse,
      content?: string
    ): Promise<boolean> => {
      const success = await submitFeedback(part.id, "sys_improve", content)
      if (success) {
        successToast({
          description: t(
            "common.chat.feedback.thankYou",
            "Thank you for your feedback!"
          ),
        })
      } else {
        alertToast({
          title: t("common.chat.feedback.failed", "Failed to submit feedback"),
        })
      }
      return success
    },
    [alertToast, submitFeedback, successToast, t]
  )

  const handleFeedbackBlocked = useCallback(
    (reason: FeedbackBlockedReason) => {
      switch (reason) {
        case "helpfulAlreadySubmitted":
          alertToast({
            description: t(
              "common.chat.feedback.helpfulAlreadySubmitted",
              "You've already submitted helpful feedback for this message"
            ),
          })
          break
        case "improveAlreadySubmitted":
          alertToast({
            description: t(
              "common.chat.feedback.improveAlreadySubmitted",
              "You've already submitted improvement feedback for this message"
            ),
          })
          break
      }
    },
    [alertToast, t]
  )

  const handlePaymentRequired = useCallback(() => {
    setShowPaymentRequired(false)
    navigate(
      (billingUiEnabled ?? true)
        ? SETTINGS_ENDPOINTS.organizationPlan
        : SETTINGS_ENDPOINTS.organizationGeneral
    )
  }, [billingUiEnabled, navigate])

  const displayedParts =
    hasBeforeFilter || hasThroughPartFilter ? filteredParts : parts
  const latestDisplayedPart = displayedParts.at(-1)
  const displayedPartsScrollKey = [
    displayedParts.length,
    latestDisplayedPart?.id ?? "none",
    latestDisplayedPart?.updated_at ??
      latestDisplayedPart?.created_at ??
      "unknown",
  ].join(":")
  const isPlatformReplySubmitting =
    createPlatformReplyStatus === FetchStatus.Loading
  const isLiveTestHistory = isLiveTestCompatibilityHistory(currentHistory)
  const platformReplyCandidate = Boolean(
    !isLiveTestHistory && currentHistory?.externalUserId && historyAgentId
  )
  const canSendPlatformReply =
    !isLiveTestHistory && currentHistory?.canReply === true
  const isPlatformReplyComposerMode =
    canSendPlatformReply && composerMode === "platformReply"
  const isPollingRecovering =
    pollingFailureCount >= HUMAN_REPLY_HISTORY_POLL_FAILURE_VISIBLE_THRESHOLD
  const crmUserPath = currentHistory?.workspace
    ? currentHistory.clientUserId
      ? `/workspaces/${currentHistory.workspace.id}/users/${currentHistory.clientUserId}`
      : `/workspaces/${currentHistory.workspace.id}?tab=user-management`
    : ""
  const aiDraftTimelineGroups = useMemo(
    () => buildAIDraftTimelineGroups(aiDraftSummaries),
    [aiDraftSummaries]
  )

  useEffect(() => {
    const pendingPartId = pendingSentPlatformReplyPartIdRef.current
    if (pendingPartId === null || !hasSentPlatformReply(parts, pendingPartId))
      return

    pendingSentPlatformReplyPartIdRef.current = null
    onPlatformReplySent?.()
  }, [onPlatformReplySent, parts])

  useEffect(() => {
    if (!isPlatformReplyComposerMode) {
      handleAppliedDraftChange(null)
    }
  }, [handleAppliedDraftChange, isPlatformReplyComposerMode])

  useEffect(() => {
    if (!canSendPlatformReply) return

    let cancelled = false

    const refreshIfActive = async () => {
      if (document.visibilityState !== "visible") return
      if (isPlatformReplySubmitting || isAsking || isHitlPending) return
      if (isPollingConversationsRef.current) return

      isPollingConversationsRef.current = true
      try {
        await refreshConversationsRef.current({ silent: true })
        if (!cancelled) setPollingFailureCount(0)
      } catch {
        if (!cancelled) {
          setPollingFailureCount((count) => count + 1)
        }
      } finally {
        isPollingConversationsRef.current = false
      }
    }

    const intervalId = window.setInterval(
      () => void refreshIfActive(),
      HUMAN_REPLY_HISTORY_POLL_INTERVAL_MS
    )
    return () => {
      cancelled = true
      window.clearInterval(intervalId)
    }
  }, [canSendPlatformReply, isAsking, isHitlPending, isPlatformReplySubmitting])

  useEffect(() => {
    setPollingFailureCount(0)
    platformReplySubmitIntent.invalidate()
  }, [canSendPlatformReply, currentHistory?.id, platformReplySubmitIntent])

  useEffect(() => {
    if (isEvaluationContextView || displayedParts.length === 0) return

    let timeoutId: number | undefined
    const frameId = window.requestAnimationFrame(() => {
      const syncScroll = () => {
        triggerAutoScroll()
        checkScrollPosition()
      }

      window.requestAnimationFrame(syncScroll)
      timeoutId = window.setTimeout(syncScroll, 150)
    })

    return () => {
      window.cancelAnimationFrame(frameId)
      if (timeoutId !== undefined) window.clearTimeout(timeoutId)
    }
  }, [
    checkScrollPosition,
    displayedParts.length,
    displayedPartsScrollKey,
    isEvaluationContextView,
    triggerAutoScroll,
  ])

  const getPlatformReplyStatusLabel = useCallback(
    (status?: string, partial = false) => {
      switch (status) {
        case "sending":
          return t("history.platformReply.status.sending")
        case "sent":
          return t(
            partial
              ? "history.platformReply.status.partial"
              : "history.platformReply.status.sent"
          )
        case "failed":
          return t("history.platformReply.status.failed")
        default:
          return status || t("history.platformReply.status.unknown")
      }
    },
    [t]
  )

  const getPlatformReplyChannelLabel = useCallback((channelType?: string) => {
    if (!channelType) return undefined
    return channelType.toUpperCase()
  }, [])

  const renderPlatformReplyLabel = useCallback(
    (
      _context: { groupParts: typeof displayedParts },
      part: ConversationPartApiResponse
    ) => {
      if (!isPlatformReplyPart(part)) return null
      const status = getPlatformReplyStatus(part)
      const channel = getPlatformReplyChannelLabel(
        getPlatformReplyChannelType(part)
      )
      const errorMessage = getPlatformReplyErrorMessage(
        part.metadata.platform_reply,
        t
      )
      const actorDisplayName = getPlatformReplyActorDisplayName(part)
      const partial = isPartialPlatformReply(part)
      const statusLabel = getPlatformReplyStatusLabel(status, partial)

      return (
        <div className="mb-2 text-xs text-muted-foreground">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-medium text-foreground/75">
              {t("history.platformReply.humanOperatorMessage")}
            </span>
            {actorDisplayName && (
              <>
                <span aria-hidden="true">·</span>
                <span>{actorDisplayName}</span>
              </>
            )}
            {channel && (
              <>
                <span aria-hidden="true">·</span>
                <span>{channel}</span>
              </>
            )}
            <span aria-hidden="true">·</span>
            <span
              className={cn(
                (status === "failed" || partial) && "text-destructive",
                status === "sent" && !partial && "text-muted-foreground"
              )}
            >
              {statusLabel}
            </span>
          </div>
          {(status === "failed" || partial) && errorMessage && (
            <div className="mt-1 text-destructive">{errorMessage}</div>
          )}
          <PlatformReplyAttachment
            part={part}
            workspace={currentHistory?.workspace}
            resolveUrl={resolveAttachmentUrl}
            canResend={canSendPlatformReply}
            onChanged={() => void refreshConversations()}
          />
        </div>
      )
    },
    [
      getPlatformReplyChannelLabel,
      getPlatformReplyStatusLabel,
      t,
      currentHistory?.workspace,
      canSendPlatformReply,
      refreshConversations,
      resolveAttachmentUrl,
    ]
  )

  const conversationLoadErrorMessage = getUserFacingApiErrorMessage(
    loadConversationsError,
    t,
    t(SOMETHING_WRONG_MSG)
  )

  if (
    getHistoryError instanceof HTTPError &&
    getHistoryError.response.status === 404
  ) {
    return (
      <ErrorPage
        showButton={false}
        title={t("history.errors.notFound.title")}
        message={t("history.errors.notFound.message")}
      />
    )
  }

  if (getHistoryError && normalizedHistoryError.kind === "permission_denied") {
    return (
      <ErrorPage
        showButton={false}
        showSupportLink={false}
        title={
          normalizedHistoryError.messageKey
            ? t(normalizedHistoryError.messageKey)
            : t("common.noPermission")
        }
        message={
          normalizedHistoryError.messageKey
            ? t(normalizedHistoryError.messageKey)
            : t("common.noPermission")
        }
      />
    )
  }

  if (getHistoryError) {
    return <ErrorPage showButton={false} />
  }

  if (!currentHistory) {
    return <LoadingPage message={t(DEFAULT_LOADING_MESSAGE)} />
  }

  if (currentHistory && !currentHistory.workspace) {
    return <ErrorPage />
  }

  const historyListHref = `/workspaces/${currentHistory.workspace.id}?tab=conversations`

  return (
    <div className={cn(isEmbedded && "flex min-h-full flex-col")}>
      {!isEmbedded && (
        <div className="@container/detail-header sticky top-0 z-20 -mx-[10px] mb-3 hidden border-b border-border/70 bg-background/95 px-[10px] py-2 backdrop-blur supports-[backdrop-filter]:bg-background/80 md:-mx-[24px] md:block md:px-[24px] xl:-mx-[32px] xl:px-[32px]">
          <div className="flex min-w-0 items-center justify-between gap-4">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    asChild={true}
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
                  >
                    <Link
                      to={historyListHref}
                      aria-label={t("historyList.detail.backToList")}
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </Link>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t("historyList.detail.backToList")}
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <h1 className="max-w-[min(54vw,680px)] truncate text-base font-semibold leading-6 text-foreground">
                    {currentHistory.name || "Unknown"}
                  </h1>
                </TooltipTrigger>
                <TooltipContent>
                  {currentHistory.name || "Unknown"}
                </TooltipContent>
              </Tooltip>
            </div>

            <div className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
              <WorkspaceBadge workspaceProfile={currentHistory.workspace} />
              {currentHistory.updatedAt && (
                <>
                  <span aria-hidden="true" className="text-muted-foreground/50">
                    ·
                  </span>
                  <Clock
                    className="text-muted-foreground"
                    dateAt={currentHistory.updatedAt}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {isEmbedded && (
        <div className="@container/detail-header sticky top-0 z-10 mb-5 border-b border-border/70 bg-background px-5 py-3.5 xl:px-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1 space-y-1.5">
              <h2 className="line-clamp-2 text-lg font-semibold leading-snug tracking-tight text-foreground">
                {currentHistory.name || ""}
              </h2>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                <WorkspaceBadge workspaceProfile={currentHistory.workspace} />
                {currentHistory.updatedAt && (
                  <>
                    <span
                      aria-hidden="true"
                      className="text-muted-foreground/50"
                    >
                      ·
                    </span>
                    <Clock
                      className="text-muted-foreground"
                      dateAt={currentHistory.updatedAt}
                    />
                  </>
                )}
                {readOnly && (
                  <>
                    <span
                      aria-hidden="true"
                      className="text-muted-foreground/50"
                    >
                      ·
                    </span>
                    <span>
                      {t("history.totalCost", {
                        credits: formatCredits(
                          currentHistory.totalCostCredits ?? 0
                        ),
                      })}
                    </span>
                  </>
                )}
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-1">
              {headerActions}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    asChild={true}
                    variant="ghost"
                    size="sm"
                    className="h-8 gap-1.5 px-2.5 text-muted-foreground hover:text-foreground"
                  >
                    <Link
                      to={`/workspaces/${currentHistory.workspace.id}/histories/${currentHistory.id}`}
                      aria-label={t("historyList.detail.openFullPage")}
                    >
                      <span className="hidden text-xs font-medium @[460px]/detail-header:inline">
                        {t("historyList.detail.openFullPage")}
                      </span>
                      <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t("historyList.detail.openFullPage")}
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>
      )}

      <div
        className={cn(
          "flex flex-col gap-4 pb-32 @container/history",
          isEmbedded && "flex-1 pb-6"
        )}
      >
        <CopilotHiddenField
          fieldKey="history.title"
          value={currentHistory?.name || ""}
        />
        <CopilotHiddenField
          fieldKey="history.selectedId"
          value={currentHistory?.id ? String(currentHistory.id) : ""}
        />
        <CopilotHiddenField
          fieldKey="history.agentId"
          value={historyAgentId || ""}
        />

        {isLiveTestHistory && (
          <div className="mx-auto w-full max-w-(--breakpoint-md) px-4 pt-2 2xl:max-w-(--breakpoint-lg)">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-muted/35 px-4 py-3 text-sm">
              <div className="flex min-w-0 items-start gap-3">
                <BotIcon
                  className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
                <div>
                  <div className="font-medium text-foreground">
                    {t("history.liveTestReadOnly.title")}
                  </div>
                  <div className="mt-0.5 text-muted-foreground">
                    {t("history.liveTestReadOnly.description")}
                  </div>
                </div>
              </div>
              {currentHistory.initAgentId && (
                <Button asChild size="sm" variant="outline">
                  <Link
                    to={`/workspaces/${currentHistory.workspace.id}/agents/${currentHistory.initAgentId}`}
                  >
                    {t("history.liveTestReadOnly.openLiveTest")}
                    <ArrowUpRight className="ml-1.5 h-4 w-4" />
                  </Link>
                </Button>
              )}
            </div>
          </div>
        )}

        {displayedParts.length > 0 && (
          <WebConversationPartPanel
            className="mx-auto w-full py-4 max-w-(--breakpoint-md) 2xl:max-w-(--breakpoint-lg)"
            history={toUIHistory(currentHistory)}
            parts={displayedParts}
            isAsking={isAsking || isHitlPending}
            showExternalUserId={true}
            externalUserIdFallback={currentHistory.externalUserId}
            resolveUrl={resolveAttachmentUrl}
            onDownload={handleAttachmentDownload}
            onPreviewLoad={handleAttachmentPreviewLoad}
            assistantActionBarMode={
              isEvaluationContextView || isLiveTestHistory ? "hidden" : "custom"
            }
            onHelpfulFeedback={handleHelpfulFeedback}
            onImproveFeedback={handleImproveFeedback}
            onFeedbackBlocked={handleFeedbackBlocked}
            submittingFormRequestId={submittingFormRequestId}
            onSubmitFormInteraction={
              isLiveTestHistory ? undefined : submitFormInteraction
            }
            onRejectFormInteraction={
              isLiveTestHistory ? undefined : rejectFormInteraction
            }
            onCreateEvalPart={
              isLiveTestHistory ? undefined : handleCreateEvalFromParts
            }
            renderPartLabel={renderPlatformReplyLabel}
            renderPart={(_context, part, defaultNode) => {
              if (
                (part.part_kind === "tool-call" ||
                  part.part_kind === "tool-return") &&
                part.metadata.source === "ai_draft" &&
                part.metadata.visibility === "console_only"
              ) {
                // The selected Draft version renders its own activity snapshots.
                // Use a non-null empty node so renderPart does not restore the default tool card.
                return <></>
              }
              if (part.id == null) return defaultNode
              const draftGroup = aiDraftTimelineGroups.correlatedByPartId.get(
                part.id
              )
              if (!draftGroup) return defaultNode
              return (
                <AIDraftVersionSwitcher
                  draftTarget={draftTarget}
                  summaries={draftGroup}
                  actualNode={defaultNode}
                />
              )
            }}
            copilotFieldPrefix="history.conversation_part"
            renderPaymentSummary={(
              context,
              _message,
              paymentSummary,
              defaultNode
            ) => {
              if (
                pendingPayment?.interactionType !== "payment" ||
                pendingPayment.conversationGroupId !== context.groupId ||
                paymentSummary.requestNumber.trim() !==
                  pendingPayment.payment.merchantOrderNo?.trim()
              ) {
                return defaultNode
              }

              return (
                <PendingInteractionPaymentCard
                  pendingPayment={pendingPayment}
                  onResolved={handleResolvePaymentInteraction}
                />
              )
            }}
            renderBetweenParts={(context, part) => {
              const draftGroup =
                part.id == null
                  ? undefined
                  : aiDraftTimelineGroups.uncorrelatedByAnchorId.get(part.id)
              let pendingNode = null
              const pendingGroupId = pendingPayment?.conversationGroupId
              if (pendingGroupId) {
                const isTargetGroup = context.groupId === pendingGroupId
                const lastMessage = context.groupParts
                  .filter(
                    (item) =>
                      item.part_kind === "user-prompt" ||
                      item.part_kind === "text"
                  )
                  .at(-1)
                const isAfterLastMessage =
                  lastMessage != null && lastMessage === part
                if (
                  isTargetGroup &&
                  isAfterLastMessage &&
                  pendingPayment?.interactionType === "payment"
                ) {
                  const requestNumber =
                    pendingPayment.payment.merchantOrderNo?.trim()
                  const hasInlineSummary = context.groupParts.some((item) => {
                    if (item.part_kind !== "text") return false
                    const content =
                      typeof item.content.content === "string"
                        ? item.content.content
                        : ""
                    return (
                      parsePaymentSummaryContent(
                        content
                      )?.requestNumber.trim() === requestNumber
                    )
                  })
                  if (!hasInlineSummary) {
                    pendingNode = (
                      <PendingInteractionPaymentCard
                        pendingPayment={pendingPayment}
                        onResolved={handleResolvePaymentInteraction}
                      />
                    )
                  }
                }
              }

              if (!pendingNode && !draftGroup) return null
              return (
                <>
                  {pendingNode}
                  {draftGroup && (
                    <AIDraftVersionSwitcher
                      draftTarget={draftTarget}
                      summaries={draftGroup}
                      className="mt-6"
                    />
                  )}
                </>
              )
            }}
          />
        )}

        {displayedParts.length === 0 && Boolean(loadConversationsError) && (
          <div className="mx-auto w-full max-w-(--breakpoint-md) px-4 py-4 2xl:max-w-(--breakpoint-lg)">
            <div className="rounded-md border border-amber-200/80 bg-amber-50/70 px-4 py-3 text-sm font-medium text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100">
              {conversationLoadErrorMessage}
            </div>
          </div>
        )}

        {(isAsking || isHitlPending) &&
          currentContext &&
          parts.length === 0 && <Separator className="w-full mt-16 mb-4" />}
      </div>

      {!isEvaluationContextView && !readOnly && (
        <div
          className={cn(
            "z-10",
            isEmbedded
              ? "sticky bottom-0 border-t border-border/70 bg-background px-4 pb-3 pt-2 xl:px-5"
              : "sticky bottom-3"
          )}
        >
          <ScrollToBottomButton
            className="z-30"
            show={showScrollToBottom}
            onClick={scrollToBottom}
            position="absolute"
            bottom="calc(100% + 8px)"
          />
          <div
            className={cn(
              "mx-auto flex w-full max-w-(--breakpoint-md) flex-wrap items-center justify-between gap-2 px-4 pb-2 text-xs font-medium text-muted-foreground 2xl:max-w-(--breakpoint-lg)",
              isEmbedded && "px-1 pr-8"
            )}
          >
            {currentHistory.workspace.organizationId &&
              currentHistory.channel?.type === "line" && (
                <div className="w-full">
                  <LineQuotaNotice
                    organizationId={currentHistory.workspace.organizationId}
                    workspaceId={currentHistory.workspace.id}
                    conversationKey={String(currentHistory.id)}
                    channel={currentHistory.channel}
                  />
                </div>
              )}
            <PlatformReplyComposerMeta
              canSendPlatformReply={canSendPlatformReply}
              canUseFollowUp={false}
              isPollingRecovering={isPollingRecovering}
              replyDisabledReason={currentHistory?.replyDisabledReason}
              platformReplyCandidate={platformReplyCandidate}
              composerMode={composerMode}
              onComposerModeChange={setComposerMode}
              onOpenCrm={() => navigate(crmUserPath)}
            />
            <div className="ml-auto flex items-center gap-2">
              <Coins className="h-3.5 w-3.5 opacity-70" aria-hidden="true" />
              <span>
                {t("history.totalCost", {
                  credits: formatCredits(currentHistory.totalCostCredits ?? 0),
                })}
              </span>
            </div>
          </div>
          {isPlatformReplyComposerMode && (
            <div
              data-copilot-field="threadPanel.input"
              className={cn(
                "mx-auto w-full max-w-(--breakpoint-md) p-4 2xl:max-w-(--breakpoint-lg)",
                isEmbedded && "py-0 pl-0 pr-8"
              )}
            >
              <div className="rounded-xl border border-border/80 bg-background p-3 shadow-[0_1px_3px_rgba(15,23,42,0.04),0_4px_12px_-6px_rgba(15,23,42,0.08)] transition-shadow focus-within:border-border focus-within:shadow-[0_1px_3px_rgba(15,23,42,0.06),0_8px_20px_-8px_rgba(15,23,42,0.12)]">
                <AIDraftPlatformReplyComposer
                  draftTarget={draftTarget}
                  historyId={currentHistory.id}
                  channelType={currentHistory.channel?.type}
                  onAttachmentChange={() =>
                    platformReplySubmitIntent.invalidate()
                  }
                  message={platformReplyText}
                  currentContextAnchor={replyContextAnchor}
                  appliedDraftContext={appliedDraftContext}
                  isSubmitting={isPlatformReplySubmitting}
                  unsupportedTools={aiDraftToolSupport?.unsupportedTools}
                  toolSupportStatus={
                    aiDraftToolSupport?.status ?? "unavailable"
                  }
                  textareaRef={platformReplyTextareaRef}
                  placeholder={t("history.platformReply.placeholder")}
                  sendLabel={t("history.platformReply.send")}
                  submittingLabel={t("history.platformReply.sending")}
                  notice={
                    platformReplyNotice ? (
                      <div
                        className={cn(
                          "mb-3 rounded-md border px-3 py-2 text-sm",
                          platformReplyNotice.type === "failed"
                            ? "border-destructive/30 bg-destructive/10 text-destructive"
                            : "border-amber-300/70 bg-amber-50 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200"
                        )}
                      >
                        {platformReplyNotice.message}
                      </div>
                    ) : undefined
                  }
                  onMessageChange={handlePlatformReplyTextChange}
                  onAppliedDraftChange={handleAppliedDraftChange}
                  onRecordsChanged={refreshAIDrafts}
                  onBillingChanged={() =>
                    onFinishConversationCallback(historyId)
                  }
                  onPermissionDenied={refreshCurrentHistoryPermissions}
                  onSend={(id, accepted) =>
                    void handleSendPlatformReply(id, accepted)
                  }
                />
              </div>
            </div>
          )}
        </div>
      )}

      <PaymentRequiredDialog
        open={showPaymentRequired}
        onOpenChange={setShowPaymentRequired}
        onConfirm={handlePaymentRequired}
      />

      {evalCaseData && !isEvaluationContextView && (
        <CaseDetailModal
          isOpen={isEvalModalOpen}
          onClose={handleCloseEvalModal}
          caseData={evalCaseData}
          evaluators={evaluators}
          initialEvaluator={evaluators.length > 0 ? evaluators[0] : undefined}
          workspaceId={currentHistory?.workspace?.id}
          evaluationResult={undefined}
          onFieldChange={undefined}
          onRunSingleCase={undefined}
          selectedAgentHistory={null}
          isCreateMode={true}
          labels={labels}
        />
      )}
    </div>
  )
}

export default HistoryDetail
